import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { orders } from '../data/mockData';
export function OrderDetails() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const order = orders.find(o => o.id === id);
  const [isDelivered, setIsDelivered] = useState(order?.status === 'completed');
  if (!order) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-227">
        <p className="text-[#64748B]" data-id="element-228">Order not found</p>
      </div>;
  }
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const handleMarkDelivered = () => {
    setIsDelivered(true);
  };
  const timelineSteps = [{
    label: 'Payment Received',
    completed: true
  }, {
    label: 'Processing',
    completed: order.status === 'processing' || isDelivered
  }, {
    label: 'Completed',
    completed: isDelivered
  }];
  return <div className="min-h-screen pb-10" data-id="element-229">
      <div className="max-w-md mx-auto" data-id="element-230">
        {/* Header */}
        <div className="flex items-center gap-4 p-5 pt-8" data-id="element-231">
          <button onClick={() => navigate('/orders')} className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-white hover:bg-white/[0.08] transition-colors" data-id="element-232">
            <ArrowLeft className="w-5 h-5" data-id="element-233" />
          </button>
          <h1 className="text-white text-2xl font-bold tracking-tight" data-id="element-234">
            Order Details
          </h1>
        </div>

        <div className="px-5 space-y-4" data-id="element-235">
          {/* Order Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="glass-card p-6" data-id="element-236">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/[0.06]" data-id="element-237">
              <div data-id="element-238">
                <p className="text-label mb-1" data-id="element-239">Order ID</p>
                <p className="text-white font-mono font-bold tracking-tight" data-id="element-240">
                  {order.id}
                </p>
              </div>
              <div className="text-right" data-id="element-241">
                <p className="text-label mb-1" data-id="element-242">Date</p>
                <p className="text-white text-sm font-medium" data-id="element-243">
                  {formatDate(order.date).split(',')[0]}
                </p>
              </div>
            </div>

            <div className="mb-6" data-id="element-244">
              <p className="text-label mb-1" data-id="element-245">Product</p>
              <p className="text-white text-lg font-bold tracking-tight" data-id="element-246">
                {order.productName}
              </p>
            </div>

            <div className="mb-6" data-id="element-247">
              <p className="text-label mb-1" data-id="element-248">Buyer</p>
              <p className="text-white font-medium" data-id="element-249">{order.buyerName}</p>
            </div>

            <div className="pt-4 border-t border-white/[0.06]" data-id="element-250">
              <p className="text-label mb-1" data-id="element-251">Amount Paid</p>
              <p className="text-[#00D68F] text-3xl font-bold tracking-tight" data-id="element-252">
                ₦{order.amount.toLocaleString()}
              </p>
            </div>
          </motion.div>

          {/* Status Timeline */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="glass-card p-6" data-id="element-253">
            <h3 className="text-label mb-6" data-id="element-254">Order Status</h3>

            <div className="space-y-6" data-id="element-255">
              {timelineSteps.map((step, index) => <motion.div key={step.label} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.2 + index * 0.1
            }} className="flex items-start gap-4" data-id="element-256">
                  <div className="relative" data-id="element-257">
                    <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.3 + index * 0.1,
                  type: 'spring'
                }} className={`w-10 h-10 rounded-full flex items-center justify-center z-10 relative ${step.completed ? 'bg-[#00D68F] shadow-[0_0_15px_rgba(0,214,143,0.3)]' : 'bg-white/[0.04] border border-white/[0.1]'}`} data-id="element-258">
                      {step.completed ? <CheckCircle className="w-5 h-5 text-[#0A0E1A]" data-id="element-259" /> : <Clock className="w-5 h-5 text-[#64748B]" data-id="element-260" />}
                    </motion.div>
                    {index < timelineSteps.length - 1 && <div className={`absolute left-5 top-10 w-0.5 h-8 -ml-[1px] ${step.completed ? 'bg-[#00D68F]/50' : 'bg-white/[0.06]'}`} data-id="element-261" />}
                  </div>
                  <div className="flex-1 pt-2" data-id="element-262">
                    <p className={`font-bold tracking-tight ${step.completed ? 'text-white' : 'text-[#64748B]'}`} data-id="element-263">
                      {step.label}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          {/* Action Button */}
          {!isDelivered && <motion.button initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} whileTap={{
          scale: 0.97
        }} onClick={handleMarkDelivered} className="w-full bg-[#00D68F] text-[#0A0E1A] rounded-2xl py-4 font-bold text-lg shadow-[0_0_20px_rgba(0,214,143,0.2)]" data-id="element-264">
              Mark as Delivered
            </motion.button>}

          {isDelivered && <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="w-full glass-card border-[#00D68F]/30 text-[#00D68F] py-4 font-bold text-lg text-center flex items-center justify-center gap-2" data-id="element-265">
              <CheckCircle className="w-6 h-6" data-id="element-266" />
              Delivered
            </motion.div>}
        </div>
      </div>
    </div>;
}